import React from "react";
import { ButtonComponentsType, ButtonPropsType, ButtonStylesType } from "./types";
import styled from "styled-components";
import ButtonStyles from "./styles";
import { useSuiTheme } from "baseStyles/ThemeProvider";
import { ITheme } from "baseStyles/ThemeProvider/themes";
import { useClickThrottle } from "hooks/useClickThrottle";
import { Colors } from "baseStyles/enums/colors";

const getBackground = (props: ButtonPropsType, theme: ITheme): ButtonPropsType => {
    if(props.primary) return {
        backgroundColor: "#4F52C3",
        hover: {
            backgroundColor: "#34389F",
        }
    };
    if(props.secondary) return {
        backgroundColor: "#BDBFF9"
    };
    if(props.primaryFilled) return {
        backgroundColor: theme.primaryFillButton,
        hover: {
            backgroundColor: theme.primaryFillButtonHover,
        }
    };
    if(props.secondaryFilled) return {
        backgroundColor: theme.secondaryFillButton,
        hover: {
            backgroundColor: theme.secondaryFillButtonHover,
        }
    };
    if(props.bordered) return {
        backgroundColor: "transparent",
        border: `2px solid ${Colors.PURE_PURPLE} !important`,
        shadowWidth: 0.001,
        hover: {
            backgroundColor: "#9D9FFC33",
            border: props.borderColorHover ? `2px solid ${props.borderColorHover} !important` : `2px solid ${Colors.NAVY_BLUE} !important`,
        }
    }
    return {
        backgroundColor: "#EDEDF9"
    };
}

const StyledButton = styled.button<ButtonPropsType>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    border-radius: 800px;
    box-shadow: ${props => `0px -${props.shadowWidth || 6}px 0px 0px rgba(0, 0, 0, 0.25) inset !important`};
    height: ${props => props.height};
    padding: ${props => props.padding};
    background-color: ${props => props.backgroundColor};
    ${props => props.margin && `margin: ${props.margin}`};
    border: ${props => props.border || 0};
    outline: 0;
    box-sizing: border-box;
    width: ${props => props.width || "100%"};
    transition: all 0.25s;
    padding-bottom: ${props => props.shadowWidth}px;
    padding-top: 0px;

    :hover {
        background-color: ${props => props?.hover?.backgroundColor};
        cursor: ${props => props?.hover?.cursor};
        ${props => props?.hover?.border && `border: ${props?.hover?.border}`};
    };

    :focus {
        box-shadow: ${props => `0px ${props.shadowWidth || 6}px 0px 0px rgba(0, 0, 0, 0.25) inset !important`};
        padding-bottom: 0px;
        padding-top: ${props => props.shadowWidth}px;
        ${props => props.borderColorFocus && props.bordered && `border: 2px solid ${props.borderColorFocus} !important`};
        ${props => props.backgroundColorFocus && `background-color: ${props.backgroundColorFocus} !important`};
    };
    ${props => `${props?.disabled && `
        pointer-events: none;
        opacity: 0.4;
    `}`};
`;

const Button: React.FC<ButtonPropsType> & ButtonComponentsType = ({type, children, onClick, icon, timeout, disabled = false, dataTestId, ...props}) => {
    const styles = ButtonStyles();
    const { theme } = useSuiTheme();
    const background = getBackground(props, theme)
    const throttledOnClick = useClickThrottle(onClick, timeout);

    return <StyledButton data-test-id={dataTestId} onClick={throttledOnClick} className={`sui-button`} {...(styles[type] || styles.L)} {...background} {...props} disabled={disabled}>{icon}{children}</StyledButton>
}

Button[ButtonStylesType.XL] = (props) => <Button type={ButtonStylesType.XL} {...props}/>;
Button[ButtonStylesType.L] = (props) => <Button type={ButtonStylesType.L} {...props}/>;
Button[ButtonStylesType.M] = (props) => <Button type={ButtonStylesType.M} {...props}/>;
Button[ButtonStylesType.S] = (props) => <Button type={ButtonStylesType.S} {...props} />;
Button[ButtonStylesType.XS] = (props) => <Button type={ButtonStylesType.XS} {...props} />;

export default Button;